import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { useColorModeValue } from '@/components/ui/color-mode'
import { Slider } from '@/components/ui/slider'
import { MAX_OUTPUT_LONG_EDGE } from '@/utils/exportCroppedImage'

export type BlurBrushImperativeHandle = {
  undo: () => void
  redo: () => void
  getCanvas: () => HTMLCanvasElement | null
  canUndo: () => boolean
  canRedo: () => boolean
  /** Commit in-progress stroke (e.g. before leaving blur mode). */
  commitPendingStroke: () => void
  /** At least one brush stroke (including unreleased stroke). */
  hasModifications: () => boolean
}

type Stroke = {
  points: [number, number][]
  radius: number
  blurPx: number
}

interface BlurSelectionProps {
  imageSrc: string
}

const MAX_STROKE_HISTORY = 24
const DEFAULT_BLUR_PX = 12

function interpolatePoints(
  from: [number, number],
  to: [number, number],
  spacing: number
): [number, number][] {
  const dx = to[0] - from[0]
  const dy = to[1] - from[1]
  const dist = Math.hypot(dx, dy)
  if (dist < spacing) {
    return [to]
  }
  const n = Math.ceil(dist / spacing)
  const out: [number, number][] = []
  for (let i = 1; i <= n; i++) {
    const t = i / n
    out.push([from[0] + dx * t, from[1] + dy * t])
  }
  return out
}

export const BlurSelection = forwardRef<
  BlurBrushImperativeHandle,
  BlurSelectionProps
>(({ imageSrc }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const workSizeRef = useRef({ w: 0, h: 0 })
  const strokesRef = useRef<Stroke[]>([])
  const redoRef = useRef<Stroke[]>([])
  const currentStrokeRef = useRef<[number, number][]>([])
  const isDrawingRef = useRef(false)
  const lastPointRef = useRef<[number, number] | null>(null)
  const brushRadiusRef = useRef(36)
  const [, force] = useState(0)
  const bump = useCallback(() => force((n) => n + 1), [])
  const { t } = useTranslation()
  const panelBg = useColorModeValue('gray.50', 'whiteAlpha.50')
  const panelBorder = useColorModeValue('gray.100', 'whiteAlpha.100')
  const sectionLabelColor = useColorModeValue('gray.600', 'gray.400')

  const [brushRadius, setBrushRadius] = useState(36)

  useEffect(() => {
    brushRadiusRef.current = brushRadius
  }, [brushRadius])

  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current
    const img = imageRef.current
    if (!canvas || !img) {
      return
    }
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return
    }
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    const { w, h } = workSizeRef.current
    if (w === 0 || h === 0) {
      return
    }
    ctx.clearRect(0, 0, w, h)
    ctx.drawImage(img, 0, 0, w, h)

    const paintPoints = (
      points: [number, number][],
      radius: number,
      blurPx: number
    ) => {
      for (const [px, py] of points) {
        ctx.save()
        ctx.beginPath()
        ctx.arc(px, py, radius, 0, Math.PI * 2)
        ctx.clip()
        ctx.filter = `blur(${blurPx}px)`
        ctx.drawImage(img, 0, 0, w, h)
        ctx.restore()
      }
    }

    for (const stroke of strokesRef.current) {
      paintPoints(stroke.points, stroke.radius, stroke.blurPx)
    }
    paintPoints(
      currentStrokeRef.current,
      brushRadiusRef.current,
      DEFAULT_BLUR_PX
    )
  }, [])

  useEffect(() => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.src = imageSrc

    image.onload = () => {
      imageRef.current = image
      const nw = image.naturalWidth || image.width
      const nh = image.naturalHeight || image.height
      const long = Math.max(nw, nh)
      const scale =
        long > MAX_OUTPUT_LONG_EDGE ? MAX_OUTPUT_LONG_EDGE / long : 1
      const w = Math.max(1, Math.round(nw * scale))
      const h = Math.max(1, Math.round(nh * scale))
      workSizeRef.current = { w, h }
      const canvas = canvasRef.current
      if (canvas) {
        canvas.width = w
        canvas.height = h
        strokesRef.current = []
        redoRef.current = []
        currentStrokeRef.current = []
        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = 'high'
          ctx.drawImage(image, 0, 0, w, h)
        }
        bump()
      }
    }

    return () => {
      image.onload = null
    }
  }, [imageSrc, bump])

  const canvasToLocal = useCallback(
    (clientX: number, clientY: number): [number, number] | null => {
      const canvas = canvasRef.current
      if (!canvas) {
        return null
      }
      const rect = canvas.getBoundingClientRect()
      const x = ((clientX - rect.left) / rect.width) * canvas.width
      const y = ((clientY - rect.top) / rect.height) * canvas.height
      return [x, y]
    },
    []
  )

  const commitCurrentStroke = useCallback(() => {
    const pts = currentStrokeRef.current
    if (pts.length === 0) {
      return
    }
    strokesRef.current.push({
      points: pts,
      radius: brushRadiusRef.current,
      blurPx: DEFAULT_BLUR_PX,
    })
    if (strokesRef.current.length > MAX_STROKE_HISTORY) {
      strokesRef.current.shift()
    }
    redoRef.current = []
    currentStrokeRef.current = []
    renderCanvas()
    bump()
  }, [renderCanvas, bump])

  const undo = useCallback(() => {
    const s = strokesRef.current
    if (s.length === 0) {
      return
    }
    const last = s.pop()!
    redoRef.current.push(last)
    renderCanvas()
    bump()
  }, [renderCanvas, bump])

  const redo = useCallback(() => {
    const r = redoRef.current
    if (r.length === 0) {
      return
    }
    const stroke = r.pop()!
    strokesRef.current.push(stroke)
    renderCanvas()
    bump()
  }, [renderCanvas, bump])

  const commitPendingStroke = useCallback(() => {
    isDrawingRef.current = false
    lastPointRef.current = null
    commitCurrentStroke()
  }, [commitCurrentStroke])

  useImperativeHandle(
    ref,
    () => ({
      undo,
      redo,
      getCanvas: () => canvasRef.current,
      canUndo: () => strokesRef.current.length > 0,
      canRedo: () => redoRef.current.length > 0,
      commitPendingStroke,
      hasModifications: () =>
        strokesRef.current.length > 0 || currentStrokeRef.current.length > 0,
    }),
    [undo, redo, commitPendingStroke]
  )

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId)
      const p = canvasToLocal(e.clientX, e.clientY)
      if (!p) {
        return
      }
      isDrawingRef.current = true
      lastPointRef.current = p
      currentStrokeRef.current = [p]
      renderCanvas()
    },
    [canvasToLocal, renderCanvas]
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current || !lastPointRef.current) {
        return
      }
      const p = canvasToLocal(e.clientX, e.clientY)
      if (!p) {
        return
      }
      const spacing = Math.max(4, brushRadiusRef.current * 0.35)
      const extra = interpolatePoints(lastPointRef.current, p, spacing)
      currentStrokeRef.current.push(...extra)
      lastPointRef.current = p
      renderCanvas()
    },
    [canvasToLocal, renderCanvas]
  )

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }
      if (!isDrawingRef.current) {
        return
      }
      isDrawingRef.current = false
      lastPointRef.current = null
      commitCurrentStroke()
    },
    [commitCurrentStroke]
  )

  return (
    <Box>
      <Box
        borderRadius="xl"
        borderWidth="1px"
        borderColor={panelBorder}
        bg={panelBg}
        px={4}
        py={3}
        mb={3}
      >
        <VStack align="stretch" gap={2}>
          <Text
            fontSize="10px"
            fontWeight="semibold"
            color={sectionLabelColor}
            letterSpacing="0.08em"
            textTransform="uppercase"
          >
            {t('media.image.actions.blur.brushSize')}
          </Text>
          <HStack gap={3} alignItems="center">
            <Slider
              min={8}
              max={120}
              step={2}
              value={[brushRadius]}
              onValueChange={(d) => setBrushRadius(d.value[0])}
              flex={1}
            />
            <Text
              fontSize="xs"
              color="fg.muted"
              minW="48px"
              textAlign="right"
              fontVariantNumeric="tabular-nums"
            >
              {t('media.image.actions.blur.radiusPixels', {
                pixels: brushRadius,
              })}
            </Text>
          </HStack>
        </VStack>
      </Box>
      <canvas
        ref={canvasRef}
        style={{
          cursor: 'crosshair',
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          touchAction: 'none',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
    </Box>
  )
})

BlurSelection.displayName = 'BlurSelection'
